import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { UserPlus, BellRinging, Check, X, Clock, Users, QrCode } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

export default function WaitlistPage() {
  const [waitlist, setWaitlist] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ guest_name: "", guest_phone: "", party_size: 2, notes: "" });
  const [waitTime, setWaitTime] = useState(null);
  const [qrItem, setQrItem] = useState(null);

  const fetchWaitlist = useCallback(async () => {
    try {
      const { data } = await api.get("/waitlist");
      setWaitlist(data);
    } catch { toast.error("Failed to load waitlist"); }
  }, []);

  useEffect(() => { fetchWaitlist(); }, [fetchWaitlist]);

  // WebSocket
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "waitlist_update") fetchWaitlist();
      };
    } catch { /* silent */ }
    return () => ws?.close();
  }, [fetchWaitlist]);

  const addGuest = async () => {
    if (!form.guest_name.trim()) return;
    try {
      await api.post("/waitlist", form);
      setForm({ guest_name: "", guest_phone: "", party_size: 2, notes: "" });
      setShowAdd(false);
      fetchWaitlist();
      toast.success("Guest added to waitlist");
    } catch { toast.error("Failed to add guest"); }
  };

  const notifyGuest = async (id) => {
    try {
      const { data } = await api.post(`/waitlist/${id}/notify`);
      fetchWaitlist();
      toast.success(data.sms_sent ? "SMS sent!" : "Guest notified (SMS not configured)");
    } catch { toast.error("Failed to notify"); }
  };

  const seatGuest = async (id) => {
    try {
      await api.patch(`/waitlist/${id}`, { status: "seated" });
      fetchWaitlist();
      toast.success("Guest seated");
    } catch { toast.error("Failed"); }
  };

  const removeGuest = async (id) => {
    try {
      await api.delete(`/waitlist/${id}`);
      fetchWaitlist();
      toast.success("Removed from waitlist");
    } catch { toast.error("Failed"); }
  };

  const getWaitTime = async () => {
    try {
      const { data } = await api.get(`/waittime?party_size=${form.party_size || 2}`);
      setWaitTime(data);
    } catch { /* silent */ }
  };

  const statusStyle = {
    waiting: { bg: "#F4A26120", color: "#F4A261", label: "Waiting" },
    notified: { bg: "#81B29A20", color: "#81B29A", label: "Notified" },
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="waitlist-page">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Waitlist</h2>
          <p className="text-xs sm:text-sm text-stone-500 mt-1">{waitlist.length} guest{waitlist.length !== 1 ? "s" : ""} waiting</p>
        </div>
        <Button onClick={() => setShowAdd(true)} style={{ backgroundColor: "#2F4F4F" }} data-testid="add-guest-btn" className="text-xs sm:text-sm">
          <UserPlus size={18} className="mr-1 sm:mr-1.5" /> <span className="hidden sm:inline">Add</span> Guest
        </Button>
      </div>

      {waitlist.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <img src="https://images.unsplash.com/photo-1673663095512-b188c4604a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwZm9vZCUyMHBsYXRpbmd8ZW58MHx8fHwxNzc2MTY1Mzg4fDA&ixlib=rb-4.1.0&q=85" alt="empty" className="w-32 h-32 rounded-full object-cover mx-auto mb-4 opacity-60" />
          <p className="text-stone-500 text-sm">No guests waiting</p>
        </div>
      ) : (
        <div className="space-y-3">
          {waitlist.map((item, idx) => {
            const st = statusStyle[item.status] || statusStyle.waiting;
            return (
              <div key={item.id} className="bg-white rounded-lg border border-stone-200 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 animate-fade-in" style={{ animationDelay: `${idx * 50}ms` }} data-testid={`waitlist-item-${item.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: "#2F4F4F" }}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-stone-800 text-sm sm:text-base">{item.guest_name}</span>
                      <Badge style={{ backgroundColor: st.bg, color: st.color, border: "none" }}>{st.label}</Badge>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs text-stone-500 flex-wrap">
                      <span className="flex items-center gap-1"><Users size={12} /> Party of {item.party_size}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> ~{item.estimated_wait} min</span>
                      {item.guest_phone && <span className="hidden sm:inline">{item.guest_phone}</span>}
                    </div>
                    {item.notes && <p className="text-xs text-stone-500 mt-1">{item.notes}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 ml-12 sm:ml-0">
                  <Button variant="ghost" size="sm" onClick={() => setQrItem(item)} data-testid={`qr-btn-${item.id}`} className="text-xs text-stone-500 hover:text-stone-700">
                    <QrCode size={14} />
                  </Button>
                  {item.status === "waiting" && (
                    <Button variant="outline" size="sm" onClick={() => notifyGuest(item.id)} data-testid={`notify-btn-${item.id}`} className="text-xs">
                      <BellRinging size={14} className="mr-1" /> Notify
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => seatGuest(item.id)} data-testid={`seat-btn-${item.id}`} className="text-xs text-green-700 hover:bg-green-50">
                    <Check size={14} className="mr-1" /> Seat
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => removeGuest(item.id)} data-testid={`remove-btn-${item.id}`} className="text-xs text-red-500 hover:bg-red-50">
                    <X size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-guest-dialog">
          <DialogHeader><DialogTitle>Add to Waitlist</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Member(s) Name</Label>
              <Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Name" className="mt-1.5" data-testid="guest-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Phone</Label>
              <Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} placeholder="+1234567890" className="mt-1.5" data-testid="guest-phone-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Party Size</Label>
              <Input type="number" min={1} max={20} value={form.party_size} onChange={(e) => { setForm({ ...form, party_size: parseInt(e.target.value) || "" }); }} onFocus={(e) => e.target.select()} onBlur={(e) => { if (!e.target.value) setForm({ ...form, party_size: 1 }); getWaitTime(); }} className="mt-1.5" data-testid="guest-party-size-input" />
            </div>
            {waitTime && (
              <div className="p-3 rounded-lg bg-stone-50 border border-stone-200 text-sm text-stone-600 flex items-center gap-2">
                <Clock size={16} /> {waitTime.message}
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any special requests" className="mt-1.5" data-testid="guest-notes-input" />
            </div>
            <Button onClick={addGuest} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-guest-btn">Add to Waitlist</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!qrItem} onOpenChange={() => setQrItem(null)}>
        <DialogContent data-testid="qr-dialog">
          <DialogHeader><DialogTitle>Guest QR Code</DialogTitle></DialogHeader>
          {qrItem && (
            <div className="flex flex-col items-center py-4 space-y-4">
              <div className="p-4 bg-white rounded-xl border border-stone-200 shadow-sm">
                <QRCodeSVG
                  value={`${window.location.origin}/waitlist/status/${qrItem.id}`}
                  size={200}
                  fgColor="#2F4F4F"
                  level="M"
                />
              </div>
              <div className="text-center">
                <p className="font-medium text-stone-800">{qrItem.guest_name}</p>
                <p className="text-xs text-stone-500 mt-1">Party of {qrItem.party_size} &middot; ~{qrItem.estimated_wait} min</p>
              </div>
              <p className="text-xs text-stone-500 text-center max-w-[240px]">
                Guest can scan this QR code to check their live waitlist position and estimated wait time.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
